import { useState, useEffect, useMemo } from 'react'
import { cohortsAPI } from '../../services/api'
import CohortStatusBadge from './CohortStatusBadge'

export default function CalendarView({ isAdmin }) {
  const [month, setMonth] = useState(() => { const d = new Date(); d.setDate(1); return d })
  const [steps, setSteps] = useState([])
  const [onlyMine, setOnlyMine] = useState(!isAdmin)
  const [selectedDate, setSelectedDate] = useState(null)

  useEffect(() => {
    const from = formatDate(month)
    const to = formatDate(new Date(month.getFullYear(), month.getMonth() + 1, 0))
    cohortsAPI.getScheduledSteps({ from, to, onlyMine }).then(d => setSteps(d.steps || []))
  }, [month, onlyMine])

  // Agrupa las filas (una por paso) en lineas de envio por (cohorte, hora) y por dia.
  const linesByDate = useMemo(() => {
    const byDate = {}
    for (const r of steps) {
      // Backend serializa send_date (DATE) con sufijo Z; truncar a YYYY-MM-DD.
      const dateKey = (r.send_date || '').substring(0, 10)
      if (!dateKey) continue
      ;(byDate[dateKey] = byDate[dateKey] || []).push(r)
    }
    const out = {}
    for (const [date, rows] of Object.entries(byDate)) {
      out[date] = groupDayLines(rows)
    }
    return out
  }, [steps])

  const days = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1)
    const last = new Date(month.getFullYear(), month.getMonth() + 1, 0)
    const startWeekDay = (first.getDay() + 6) % 7
    const arr = []
    for (let i = 0; i < startWeekDay; i++) arr.push(null)
    for (let d = 1; d <= last.getDate(); d++) arr.push(new Date(month.getFullYear(), month.getMonth(), d))
    return arr
  }, [month])

  const todayStr = formatDate(new Date())

  return (
    <div className="flex flex-1 min-h-0">
      <div className="flex-1 p-6 flex flex-col min-h-0">
        <div className="flex justify-between items-center mb-4 flex-shrink-0">
          <div className="flex gap-3 items-center">
            <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="px-3 py-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-dark-hover rounded-lg">&#9664;</button>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{month.toLocaleString('es-CL', { month: 'long', year: 'numeric' })}</h3>
            <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="px-3 py-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-dark-hover rounded-lg">&#9654;</button>
          </div>
          {isAdmin && (
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <input type="checkbox" checked={!onlyMine} onChange={() => setOnlyMine(!onlyMine)} />
              Ver todas
            </label>
          )}
        </div>
        <div className="grid grid-cols-7 gap-2 text-xs text-gray-500 dark:text-gray-400 mb-2 flex-shrink-0">
          {['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'].map(d => <div key={d} className="text-center font-medium">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-2 flex-1 min-h-0 auto-rows-fr">
          {days.map((d, idx) => {
            if (!d) return <div key={idx} />
            const dStr = formatDate(d)
            const lines = linesByDate[dStr] || []
            const isWeekend = d.getDay() === 0 || d.getDay() === 6
            const isToday = dStr === todayStr
            const ringClass = selectedDate === dStr
              ? 'ring-2 ring-purple-500'
              : (isToday ? 'ring-1 ring-purple-300 dark:ring-purple-700' : 'hover:border-gray-400 dark:hover:border-slate-500')
            return (
              <button key={idx} onClick={() => setSelectedDate(dStr)}
                className={`min-h-0 overflow-hidden flex flex-col items-start p-2 border border-gray-200 dark:border-surface-dark-border rounded-xl text-gray-800 dark:text-gray-100 ${sendCountTint(lines.length)} ${ringClass}`}>
                <span className={`text-sm font-medium ${isWeekend ? 'text-gray-400 dark:text-gray-500' : ''}`}>{d.getDate()}</span>
                <div className="text-[10px] mt-0.5 space-y-0.5 w-full">
                  {lines.slice(0, 3).map((l, i) => (
                    <div key={i} className="truncate" title={`${fmtTime(l.send_hour, l.send_minute)} ${l.campaign_name} ${fmtSteps(l.steps)}`}>
                      {l.isArranque ? '🚀 ' : '• '}{fmtTime(l.send_hour, l.send_minute)} {l.campaign_name} {fmtSteps(l.steps)}
                    </div>
                  ))}
                  {lines.length > 3 && (
                    <div className="text-purple-600 dark:text-purple-400 font-medium">+{lines.length - 3} envíos · ver detalles</div>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {selectedDate && (
        <div className="w-80 border-l border-gray-200 dark:border-surface-dark-border p-4 bg-gray-50 dark:bg-surface-dark-elev overflow-y-auto">
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100">{selectedDate}</h4>
            <button onClick={() => setSelectedDate(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg leading-none">&times;</button>
          </div>
          {(linesByDate[selectedDate] || []).length === 0 ? (
            <div className="text-xs text-gray-400 dark:text-gray-500 italic">Sin envíos programados</div>
          ) : (
            groupByCohort(linesByDate[selectedDate]).map(cohort => (
              <DaySendsCard key={cohort.cohort_id} cohort={cohort} />
            ))
          )}
        </div>
      )}
    </div>
  )
}

// Agrupa filas (una por paso) en lineas por (cohorte, hora). isArranque = la linea incluye el Paso 1.
function groupDayLines(rows) {
  const map = new Map()
  for (const r of rows) {
    const key = `${r.cohort_id}|${r.send_hour}|${r.send_minute}`
    if (!map.has(key)) {
      map.set(key, {
        cohort_id: r.cohort_id,
        campaign_id: r.campaign_id,
        campaign_name: r.campaign_name,
        cohort_status: r.cohort_status,
        send_hour: r.send_hour,
        send_minute: r.send_minute ?? 0,
        steps: [],
      })
    }
    map.get(key).steps.push(r.step_number)
  }
  const lines = Array.from(map.values()).map(l => ({
    ...l,
    steps: l.steps.slice().sort((a, b) => a - b),
    isArranque: l.steps.includes(1),
  }))
  lines.sort((a, b) =>
    a.send_hour - b.send_hour ||
    a.send_minute - b.send_minute ||
    a.campaign_name.localeCompare(b.campaign_name)
  )
  return lines
}

// Reagrupa las lineas del dia por cohorte (para el panel lateral).
function groupByCohort(lines) {
  const map = new Map()
  for (const l of lines) {
    if (!map.has(l.cohort_id)) {
      map.set(l.cohort_id, { cohort_id: l.cohort_id, campaign_name: l.campaign_name, cohort_status: l.cohort_status, lines: [] })
    }
    map.get(l.cohort_id).lines.push(l)
  }
  return Array.from(map.values())
}

function fmtTime(h, m) {
  return `${String(h).padStart(2, '0')}:${String(m ?? 0).padStart(2, '0')}`
}

// Resumen compacto para la celda (rango min-max).
function fmtSteps(steps) {
  if (steps.length === 1) return `P${steps[0]}`
  return `P${steps[0]}-${steps[steps.length - 1]}`
}

// Detalle exacto para el panel lateral.
function fmtStepsFull(steps) {
  return steps.length === 1 ? `Paso ${steps[0]}` : `Pasos ${steps.join(', ')}`
}

// Tinte azul por numero de lineas de envio del dia (NO contactos).
function sendCountTint(n) {
  if (!n) return ''
  if (n <= 2) return 'bg-blue-50 dark:bg-blue-900/20'
  if (n <= 5) return 'bg-blue-100 dark:bg-blue-900/30'
  if (n <= 9) return 'bg-blue-200/70 dark:bg-blue-900/50'
  return 'bg-blue-300/70 dark:bg-blue-900/70'
}

function DaySendsCard({ cohort }) {
  const [expanded, setExpanded] = useState(false)
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)

  const toggle = async () => {
    if (!expanded && !detail) {
      setLoading(true)
      try {
        const d = await cohortsAPI.get(cohort.cohort_id)
        setDetail(d)
      } catch (e) {
        console.error('[DaySendsCard] Error fetching detail:', e)
      } finally {
        setLoading(false)
      }
    }
    setExpanded(!expanded)
  }

  return (
    <div className="p-3 bg-white dark:bg-surface-dark-alt rounded-xl shadow-sm mb-2">
      <div className="flex items-center gap-2 mb-2">
        <span className="font-medium text-gray-900 dark:text-gray-100 truncate">{cohort.campaign_name}</span>
        <CohortStatusBadge status={cohort.cohort_status} />
      </div>
      <ul className="text-xs space-y-1 mb-2">
        {cohort.lines.map((l, i) => (
          <li key={i} className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
            <span>{l.isArranque ? '🚀' : '•'}</span>
            <span className="font-medium">{fmtTime(l.send_hour, l.send_minute)}</span>
            <span className="text-gray-500 dark:text-gray-400">{fmtStepsFull(l.steps)}</span>
            {l.isArranque && <span className="text-[10px] text-emerald-600 dark:text-emerald-400">inicio</span>}
          </li>
        ))}
      </ul>
      <button onClick={toggle} className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 font-medium">
        {loading ? 'Cargando…' : expanded ? '▲ Ocultar inscritos' : '▼ Ver inscritos'}
      </button>
      {expanded && detail && (
        <div className="mt-2 pt-2 border-t border-gray-100 dark:border-surface-dark-border max-h-60 overflow-y-auto">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{(detail.enrollments || []).length} inscritos</div>
          {(detail.enrollments || []).length === 0 ? (
            <div className="text-xs text-gray-400 italic">Sin inscritos</div>
          ) : (
            <ul className="text-xs space-y-1">
              {(detail.enrollments || []).map(e => (
                <li key={e.id} className="flex justify-between gap-2 py-0.5">
                  <span className="truncate text-gray-700 dark:text-gray-300">{e.contact_name || e.contact_phone}</span>
                  <span className="text-gray-400 shrink-0">{e.contact_phone}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function formatDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}
